import { useEffect, useState } from 'react';

import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { PreviousThread } from './previous-thread';
import { useGetPublishedThreads } from '@/api/queries/useGetPublishedThreads';
import { useGetThreads } from '@/api/queries/useGetThreads';
import { useUserContext } from '@/Contexts/User';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/new/components/shadcn/tabs';
import { ThreadObject } from '@/types';

enum TabValue {
  Previous = 'previous',
  Published = 'published',
}

interface ThreadListProps {
  displayedItems: ThreadObject[];
  isLoading: boolean;
  hasMore: boolean;
  scrollRef: any; // IntersectionObserverHookRefCallback type
  goToThread: (threadId: string) => void;
  threadId?: string;
  activeTab: TabValue;
  userId?: string;
}

const ThreadList = ({
  displayedItems,
  isLoading,
  hasMore,
  scrollRef,
  goToThread,
  threadId,
  activeTab,
  userId,
}: ThreadListProps) => (
  <div className="flex flex-col gap-1 overflow-x-clip px-2 pb-2">
    {isLoading ? (
      <div className="flex w-52 flex-col justify-center overflow-hidden">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <Skeleton key={i} className="my-1 h-8 w-full" />
        ))}
      </div>
    ) : (
      <>
        {displayedItems.map((threadObject: ThreadObject) => (
          <PreviousThread
            key={threadObject.id}
            threadText={threadObject.name}
            threadId={threadObject.id}
            goToThread={goToThread}
            threadStatus={threadObject.status}
            isActive={threadObject.id === threadId}
            createdAt={threadObject.createdDate.toString()}
            createdByEmail={threadObject.createdBy?.email}
            createdById={threadObject.createdById}
            activeTab={activeTab}
            userId={userId}
          />
        ))}
        {hasMore && (
          <div ref={scrollRef} className="mb-4 flex items-center justify-center">
            <span className="iconify lucide--loader-circle text-brandSecondary animate-spin" />
          </div>
        )}
      </>
    )}
  </div>
);

// Tab content component
interface TabContentProps {
  value: TabValue;
  displayedItems: ThreadObject[];
  isLoading: boolean;
  hasMore: boolean;
  scrollRef: any; // IntersectionObserverHookRefCallback type
  goToThread: (threadId: string) => void;
  threadId?: string;
  activeTab: TabValue;
  userId?: string;
}

const TabContent = ({
  value,
  displayedItems,
  isLoading,
  hasMore,
  scrollRef,
  goToThread,
  threadId,
  activeTab,
  userId,
}: TabContentProps) => (
  <TabsContent value={value}>
    {displayedItems.length === 0 ? (
      <div className="mt-4 flex h-full flex-col items-center justify-center">
        <p className="text-xs text-gray-500">No {value} questions found.</p>
      </div>
    ) : (
      <ThreadList
        displayedItems={displayedItems}
        isLoading={isLoading}
        hasMore={hasMore}
        scrollRef={scrollRef}
        goToThread={goToThread}
        threadId={threadId}
        activeTab={activeTab}
        userId={userId}
      />
    )}
  </TabsContent>
);

const PageSize = 25;

export function PreviousThreads() {
  const { matterId, subsetId, threadId } = useParams();
  if (!matterId || !subsetId) return;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { user } = useUserContext();
  const { isLoading: isLoadingThreads, data: threads } = useGetThreads(subsetId);
  const { isLoading: isLoadingPublishedThreads, data: publishedThreads } = useGetPublishedThreads(subsetId);

  const [activeTab, setActiveTab] = useState(TabValue.Previous);
  const [displayedItems, setDisplayedItems] = useState((threads ?? []).slice(0, PageSize));
  const [hasMore, setHasMore] = useState(false);

  const fetchMoreData = async () => {
    // 0.8s for animation spinner
    await new Promise((resolve) => setTimeout(resolve, 800));
    const dataSource = activeTab === TabValue.Previous ? (threads ?? []) : (publishedThreads ?? []);
    if (displayedItems.length >= dataSource.length) {
      setHasMore(false);
      return;
    }
    setDisplayedItems((prevItems) => [
      ...prevItems,
      ...dataSource.slice(prevItems.length, prevItems.length + PageSize),
    ]);
  };

  const goToThread = (threadId: string) => {
    searchParams.delete('selectedDocId');
    navigate(`/matters/${matterId}/subsets/${subsetId}/kim/${threadId}`);
  };

  const [scrollRef] = useInfiniteScroll({
    loading: false,
    hasNextPage: hasMore,
    onLoadMore: fetchMoreData,
  });

  useEffect(() => {
    const dataSource = activeTab === TabValue.Previous ? (threads ?? []) : (publishedThreads ?? []);

    setDisplayedItems(dataSource.slice(0, PageSize));
    setHasMore(dataSource.length > PageSize);
  }, [threads, publishedThreads, activeTab]);

  const TABS = [
    {
      label: 'Previous',
      value: TabValue.Previous,
    },
    {
      label: 'Published',
      value: TabValue.Published,
    },
  ];

  return (
    <div className="h-full">
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)}>
        <TabsList className="w-full">
          {TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="py-2 text-xs">
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <>
          <TabContent
            value={TabValue.Previous}
            displayedItems={displayedItems}
            isLoading={isLoadingThreads}
            hasMore={hasMore}
            scrollRef={scrollRef}
            goToThread={goToThread}
            threadId={threadId}
            activeTab={activeTab}
            userId={user?.id}
          />

          <TabContent
            value={TabValue.Published}
            displayedItems={displayedItems}
            isLoading={isLoadingPublishedThreads}
            hasMore={hasMore}
            scrollRef={scrollRef}
            goToThread={goToThread}
            threadId={threadId}
            activeTab={activeTab}
            userId={user?.id}
          />
        </>
      </Tabs>
    </div>
  );
}
